import React from 'react';
import { FoodMetrics } from '../../../lib/health/types';
import { Utensils, Clock, Calendar, Camera } from 'lucide-react';

interface FoodLogCardProps {
  foodData: FoodMetrics;
}

const FoodLogCard: React.FC<FoodLogCardProps> = ({ foodData }) => {
  const getMealTypeColor = (mealType: string) => {
    switch (mealType?.toLowerCase()) {
      case 'breakfast':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'lunch':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'dinner':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'snack':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getMealIcon = (mealType: string) => {
    switch (mealType?.toLowerCase()) {
      case 'breakfast': return '🌅';
      case 'lunch': return '☀️';
      case 'dinner': return '🌙';
      case 'snack': return '🍎';
      default: return '🍽️';
    }
  };

  const getHungerDot = (hunger: string) => {
    if (hunger?.toLowerCase().includes('irritable')) return 'bg-red-500';
    if (hunger?.toLowerCase().includes('very hungry')) return 'bg-orange-500';
    if (hunger?.toLowerCase().includes('pretty hungry')) return 'bg-yellow-500';
    if (hunger?.toLowerCase().includes('little hungry')) return 'bg-yellow-400';
    if (hunger?.toLowerCase().includes('neutral')) return 'bg-gray-400';
    if (hunger?.toLowerCase().includes('just satisfied')) return 'bg-green-400';
    if (hunger?.toLowerCase().includes('completely satisfied')) return 'bg-green-500';
    if (hunger?.toLowerCase().includes('full')) return 'bg-blue-500';
    if (hunger?.toLowerCase().includes('stuffed')) return 'bg-purple-500';
    if (hunger?.toLowerCase().includes('sick')) return 'bg-red-600';
    return 'bg-gray-400';
  };

  // Utility to check if a URL is an image
  const isImageUrl = (url?: string) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url.split('?')[0]);
  };

  if (!foodData || !foodData.recentEntries || foodData.recentEntries.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Utensils className="mr-3 text-blue-500 w-5 h-5" />
          Recent Food Log
        </h3>
        <p className="text-gray-500">No recent food entries available.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Utensils className="mr-3 text-blue-500 w-5 h-5 flex-shrink-0" />
          <span>Recent Food Log</span>
        </h3>
      </div>

      {/* Entries */}
      <div className="divide-y divide-gray-100">
        {foodData.recentEntries.map((entry, index) => (
          <div key={index} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200">
            
            {/* Header: Date/Time + Meal Type */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                <span className="font-medium">{entry.date}</span>
                <Clock className="w-4 h-4 ml-3 mr-1.5 text-gray-400 flex-shrink-0" />
                <span>{entry.time}</span>
              </div>
              <div className="flex items-center gap-2">
                {entry.mealPhoto && (
                  <div 
                    className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-weight-500 flex items-center gap-1.5 cursor-pointer hover:bg-blue-600 transition-colors shadow-sm"
                    onClick={() => window.open(entry.mealPhoto, '_blank')}
                    title="View meal photo"
                  >
                    <Camera className="w-3 h-3" />
                    <span>Photo</span>
                  </div>
                )}
                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-blue-100 text-blue-700 border-blue-200">
                  <span className="mr-1.5">{getMealIcon(entry.mealType)}</span>
                  {entry.mealType}
                </div>
              </div>
            </div>

            {/* MAIN CONTENT: Food Description */}
            <div className="mb-4 flex items-center gap-2">
              <p className="text-base sm:text-lg font-medium text-gray-900 leading-relaxed mb-0">
                {entry.description}
              </p>              
            </div>

            {/* SUBTLE: Hunger indicators */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center">
                <span className="text-gray-400 mr-2">Hunger:</span>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full ${getHungerDot(entry.hungerBefore)} mr-1`}></div>
                    <span>{entry.hungerBefore}</span>
                  </div>
                  <span className="text-gray-300">→</span>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full ${getHungerDot(entry.hungerAfter)} mr-1`}></div>
                    <span>{entry.hungerAfter}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-500 text-center">
          Showing {foodData.recentEntries.length} most recent entries
        </p>
      </div>
    </div>
  );
};

export default FoodLogCard;